import React, { useCallback, useMemo, useState } from 'react';
import {
  Anchor,
  Box,
  FilledCheckIcon,
  FilledLockIcon,
  Image,
  LargeCloseIcon,
  RawButton,
  Text,
  ThinCheckIcon,
  useFormatPhoneNumber,
} from '@common-fe/common-fe';
import _ from 'lodash';

import CIPIllustration from '@/assets/dashboard/cip-illustration.svg';
import GridBackground from '@/assets/new-reports/grid-background.svg';
import { Modal } from '@/components';
import { useCurrentOrganization } from '@/modules/core/hooks';
import InfoBlock from '@/modules/Reports/WhatIsNewModal/InfoBlock';

interface Props {
  closureDelayDays?: number;
}

const CIPStatisticModal: React.FC<Props> = ({ closureDelayDays }) => {
  const [isShowed, setShowed] = useState(false);
  const { contactInfo } = useCurrentOrganization();
  const isContactEmpty = useMemo(() => {
    if (!contactInfo && _.isEmpty(contactInfo)) {
      return true;
    }
    return false;
  }, [contactInfo]);

  const handleToggleVisible = useCallback(() => {
    setShowed(!isShowed);
  }, [isShowed]);

  const formattedPhoneNumber = useFormatPhoneNumber(contactInfo?.customerServiceNumber || '');

  return (
    <>
      <Anchor
        data-testid="dashboard_statistics_cip-modal_link"
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
          handleToggleVisible();
        }}
      >
        <Text weight={400} size="medium" color="textAccent">
          CIP 101
        </Text>
      </Anchor>

      <Modal
        visible={isShowed}
        testId="dashboard_statistics_cip-modal"
        onSetVisible={handleToggleVisible}
        contentStyle={{
          padding: 0,
        }}
      >
        <Box
          style={{
            position: 'relative',
          }}
          overflow="hidden"
          round="moduleRound"
        >
          <Image
            src={GridBackground}
            style={{
              position: 'absolute',
              left: -20,
              top: -20,
              zIndex: 1,
            }}
          />
          <Box
            style={{
              zIndex: 2,
            }}
          >
            <Box pad="m" direction="row" align="center" justify="end">
              <RawButton
                data-testid="dashboard_statistics_cip-modal_close-button"
                style={{ height: '24px' }}
                onClick={handleToggleVisible}
              >
                <LargeCloseIcon color="iconPrimary" size="24px"></LargeCloseIcon>
              </RawButton>
            </Box>
            <Box direction="column" align="center" justify="center">
              <Box>
                <Text
                  data-testid="dashboard_statistics_cip-modal_title"
                  color="textTitle"
                  size="4xl"
                  textAlign="center"
                  weight={700}
                >
                  Verify Employee Identity
                </Text>
              </Box>
            </Box>
          </Box>
        </Box>

        <Box direction="column" margin={{ top: 'spacing32' }} pad={{ horizontal: 'l' }}>
          <Box
            direction="column"
            align="center"
            overflow="hidden"
            round="container2Round"
            pad={{ top: '54px', horizontal: 'xl' }}
            background="background1"
          >
            <Text size="3xl" textAlign="center" weight={700} color="textOnColor">
              Why We’re Asking
            </Text>
            <Text margin={{ top: 'spacing12' }} weight={400} textAlign="center" color="textOnColor">
              To open each employee’s HSA, we’re required to verify their identity through the Customer
              Identification Process (CIP), in compliance with the USA PATRIOT Act.
            </Text>
            <br />

            <Text weight={400} textAlign="center" color="textOnColor">
              Most employees pass CIP automatically, but a small number may need to take further steps. You
              can view a list of employees who have not passed CIP in the CIP Report, available in your admin
              dashboard.
            </Text>
            <Box margin={{ top: 'spacing24' }}>
              <Image width="280px" src={CIPIllustration} />
            </Box>
          </Box>
          <Box direction="row" margin={{ top: 's' }}>
            <Box
              direction="column"
              align="center"
              round="container2Round"
              pad={{ horizontal: 'xl', vertical: 'l' }}
              background="whatIsNew2"
              height={{ min: '420px' }}
              style={{ flex: 1 }}
            >
              <Box>
                <FilledCheckIcon size="72px" color="iconOnColor" />
                <Text
                  data-testid="dashboard_statistics_cip-modal_required-action-title"
                  size="3xl"
                  margin={{ top: 'spacing16' }}
                  color="textOnColor"
                  weight={700}
                >
                  Required Action
                </Text>
                <Text
                  weight={400}
                  margin={{ top: 'spacing16' }}
                  color="textOnColor"
                  style={{ lineHeight: '20px' }}
                >
                  Employees who don’t pass CIP will be notified and asked to answer a few short questions or
                  upload an identification document. If the employee is unable to do this themselves, you can
                  upload the required document on their behalf through the employee's profile in the admin
                  portal. Both options are fast and secure.
                </Text>
              </Box>
            </Box>
            <Box pad={{ left: 's' }}></Box>
            <Box
              align="center"
              pad={{ horizontal: 'xl', vertical: 'l' }}
              round="container2Round"
              background="whatIsNew1"
              height={{ min: '420px' }}
              style={{ flex: 1 }}
            >
              <Box direction="column">
                <FilledLockIcon size="72px" color="iconOnColor" />
                <Text
                  data-testid="dashboard_statistics_cip-modal_why-matters-title"
                  size="3xl"
                  margin={{ top: 'spacing16' }}
                  color="textOnColor"
                  weight={700}
                >
                  Why This Matters
                </Text>
                <Box direction="row" margin={{ top: 'spacing12' }}>
                  <ThinCheckIcon color="iconOnColor" />
                  <Text weight={400} size="medium" color="textOnColor">
                    Legally required to open an HSA
                  </Text>
                </Box>
                <Box direction="row" margin={{ top: 'spacing6' }}>
                  <ThinCheckIcon color="iconOnColor" />
                  <Text weight={400} size="medium" color="textOnColor">
                    Protects employee information
                  </Text>
                </Box>
                <Box direction="row" margin={{ top: 'spacing6' }}>
                  <ThinCheckIcon color="iconOnColor" />
                  <Text weight={400} size="medium" color="textOnColor">
                    Takes a minute to complete
                  </Text>
                </Box>
                <Box direction="row" margin={{ top: 'spacing6' }}>
                  <ThinCheckIcon color="iconOnColor" />
                  <Text weight={400} size="medium" color="textOnColor">
                    Ensures employees can receive employer contributions
                  </Text>
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>

        <Box direction="column" pad="l">
          <Box margin={{ bottom: 's' }}>
            <InfoBlock testId="1" title="Is this process secure?">
              Yes. All information is encrypted and protected.
            </InfoBlock>
          </Box>
          <Box margin={{ bottom: 's' }}>
            <InfoBlock testId="2" title="How long does this take?">
              Less than a minute in most cases.
            </InfoBlock>
          </Box>
          {(!!closureDelayDays || closureDelayDays == 0) && (
            <Box margin={{ bottom: 's' }}>
              <InfoBlock testId="3" title="What happens if this isn’t completed?">
                We can’t open the employee’s HSA, and they’ll miss out on any employer contributions. After
                {` ${closureDelayDays}`} days with no action, we’ll no longer be able to open the account.
              </InfoBlock>
            </Box>
          )}

          <Box margin={{ bottom: 's' }}>
            <InfoBlock testId="4" title="Where do I see who has not completed the required process?">
              You can view a list of employees who have not passed CIP in the CIP Report, available in your
              admin dashboard.
            </InfoBlock>
          </Box>

          <Box margin={{ bottom: 's' }}>
            <InfoBlock testId="5" title="How can I help?">
              Check the CIP Report in your admin dashboard and reach out to any employees listed. Ask them to
              complete the identity review process as soon as possible work with them to submit on their
              behalf.
            </InfoBlock>
          </Box>
          {!isContactEmpty && (
            <Box margin={{ bottom: 's' }}>
              <InfoBlock testId="6" title="Who do I contact if I have more questions?">
                <Box direction="column">
                  {contactInfo?.customerServiceNumber && (
                    <Anchor href={`tel:${contactInfo?.customerServiceNumber}`} target="_blank">
                      <Text color="textBody" weight={400}>
                        {formattedPhoneNumber}
                      </Text>
                    </Anchor>
                  )}

                  {contactInfo?.customerServiceEmail && (
                    <Anchor href={`mailto:${contactInfo?.customerServiceEmail}`} target="_blank">
                      <Text color="textBody" weight={400} style={{ textDecoration: 'underline' }}>
                        {contactInfo?.customerServiceEmail}
                      </Text>
                    </Anchor>
                  )}
                  {contactInfo?.customerServiceUrlLink && (
                    <Anchor href={contactInfo?.customerServiceUrlLink} target="_blank">
                      <Text color="textBody" weight={400} style={{ textDecoration: 'underline' }}>
                        {contactInfo?.customerServiceUrlName || contactInfo?.customerServiceUrlLink}
                      </Text>
                    </Anchor>
                  )}
                </Box>
              </InfoBlock>
            </Box>
          )}
        </Box>
      </Modal>
    </>
  );
};
export default CIPStatisticModal;
